import { useEffect, useState } from 'react'
import { AlertTriangle } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import Modal from './Modal'

/**
 * Modal de confirmação destrutiva para troca de Virtual Network (vnet) de um
 * ManagedDevice. Exige digitar o nome do device antes de habilitar o botão
 * confirmar — padrão type-to-confirm igual GitHub/AWS pra ações que rompem
 * conectividade.
 *
 * Lógica subjacente (vista pelo operador):
 *   1. O peer VPN atual será deletado (soft-delete; IP libertado na vnet antiga).
 *   2. Um novo peer será criado na vnet escolhida (IP novo, chaves novas).
 *   3. O device perde a VPN IMEDIATAMENTE — a config selada no TPM dele aponta
 *      pro peer antigo, que não existe mais no servidor.
 *   4. Pra restaurar conectividade, é OBRIGATÓRIO rodar de novo no device:
 *        sudo automais-device install
 *      (re-enroll PKI + recebe wgConfig nova + sela com TPM)
 */
export default function ChangeVirtualNetworkConfirmModal({
  isOpen,
  onClose,
  onConfirm,
  deviceName,
  fromNetworkName,
  toNetworkName,
  isPending = false,
}) {
  const { t } = useTranslation()
  const [typed, setTyped] = useState('')

  // Reset toda vez que o modal abrir
  useEffect(() => {
    if (isOpen) setTyped('')
  }, [isOpen])

  if (!isOpen) return null

  const canConfirm = typed.trim() === deviceName?.trim() && !isPending

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t('changeVnet.title', 'Trocar Virtual Network')}>
      <div className="flex items-start gap-3 p-3 mb-4 rounded-md border border-red-300 bg-red-50">
        <AlertTriangle className="w-6 h-6 shrink-0 text-red-600" />
        <div className="flex-1 text-sm text-red-900">
          <p className="font-semibold mb-1">
            {t('changeVnet.dangerHeading', 'Ação irreversível — interrompe a VPN do device')}
          </p>
          <p>
            {t(
              'changeVnet.dangerBody',
              'Trocar a Virtual Network deleta o peer VPN atual (libera o IP) e cria um novo na vnet escolhida. O device PERDE a conexão VPN imediatamente — a config selada no TPM dele aponta pro peer antigo, que não existirá mais.',
            )}
          </p>
        </div>
      </div>

      <ul className="text-sm text-gray-700 space-y-1.5 mb-4 list-disc list-inside">
        <li>
          {t('changeVnet.bullet1', 'O peer atual será removido (soft-delete; IP liberado em ')}
          <strong>{fromNetworkName || '—'}</strong>
          {')'}
        </li>
        <li>
          {t('changeVnet.bullet2', 'Um novo peer será criado em ')}
          <strong>{toNetworkName || '—'}</strong>
          {t('changeVnet.bullet2b', ' (IP atribuído automaticamente)')}
        </li>
        <li className="font-semibold text-red-700">
          {t(
            'changeVnet.bullet3',
            'Será obrigatório executar no device: sudo automais-device install (re-bootstrap) para receber e selar a nova configuração WireGuard.',
          )}
        </li>
        <li>
          {t(
            'changeVnet.bullet4',
            'Até o re-bootstrap rodar, o device fica offline na VPN. Acesso remoto, telemetria e qualquer serviço dependente da VPN ficam indisponíveis.',
          )}
        </li>
      </ul>

      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t('changeVnet.typeNameLabel', 'Para confirmar, digite o nome do device:')}{' '}
          <code className="px-1 py-0.5 bg-gray-100 rounded text-xs font-mono text-gray-900">
            {deviceName}
          </code>
        </label>
        <input
          type="text"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          autoFocus
          autoComplete="off"
          spellCheck={false}
          placeholder={deviceName}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 font-mono text-sm"
        />
      </div>

      <div className="flex justify-end gap-3">
        <button
          type="button"
          onClick={onClose}
          disabled={isPending}
          className="px-3 py-2 text-sm font-medium border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
        >
          {t('common.buttons.cancel', 'Cancelar')}
        </button>
        <button
          type="button"
          onClick={onConfirm}
          disabled={!canConfirm}
          className="px-3 py-2 text-sm font-semibold text-white bg-red-600 hover:bg-red-700 rounded-lg disabled:bg-red-300 disabled:cursor-not-allowed"
        >
          {t('changeVnet.confirmButton', 'Trocar e exigir re-bootstrap')}
        </button>
      </div>
    </Modal>
  )
}
